import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { createFileItem, fileStateUpdaters } from '../_utils';
import { fetchUpdateFileOrFolder } from '../apis';
import type { FileItem } from '../types';

const MAX_NAME_LENGTH = 80;

interface UseRenameFileOptions {
  item: FileItem;
  onSuccess?: (newName: string) => void;
  // 文件状态更新回调
  onFilesUpdate?: (updater: (files: FileItem[]) => FileItem[]) => void;
}

export const useRenameFile = ({ item, onSuccess, onFilesUpdate }: UseRenameFileOptions) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(item.name);
  const editingRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  const { run: renameFile, loading: renamingFile } = useFetch(
    () => fetchUpdateFileOrFolder({ path: { id: item.id }, body: { id: item.id, name: newName, type: item.type } }),
    {
      manual: true,
      onSuccess: ({ error, data }) => {
        if (error) {
          switch (error.reason) {
            case 'CommunityAuditTextFailed':
              toast({
                message: t('creations.warning.nameIllegal'),
                type: 'error',
              });
              return;
            case 'Conflict':
              toast({
                message: t('creations.warning.nameConflict'),
                type: 'error',
              });
              return;
            case 'FieldInvalid':
            case 'NewErrFieldInvalid':
              toast({
                message: `${t('creations.warning.nameInvalid')}: ${t('creations.warning.nameInvalidDescription')}`,
                type: 'error',
              });
              return;
            default:
              toast({
                message: error.message,
                type: 'error',
              });
              return;
          }
        }

        toast({
          message: t('creations.operations.renameSuccess'),
          type: 'success',
        });

        // 更新文件名和下载链接
        const newItem = createFileItem(data?.item);
        if (newItem) {
          onFilesUpdate?.((files) =>
            fileStateUpdaters.updateFile(files, item.id, {
              name: newItem.name,
              contentUrl: newItem.contentUrl,
              downloadUrl: newItem.downloadUrl,
              nomark_uri: newItem.nomark_uri,
              hasCopyright: newItem.hasCopyright,
            }),
          );
        }

        onSuccess?.(newName);
      },
    },
  );

  const handleEditName = () => {
    setIsEditingName(true);
    setNewName(item.name);
  };

  const handleRename = () => {
    setIsEditingName(false);
    if (newName.trim() === '') {
      setNewName(item.name);
      toast({
        message:
          item.type === 'folder' ? t('creations.warning.nameRequiredFolder') : t('creations.warning.nameRequiredFile'),
        type: 'error',
      });
      return;
    }
    if (newName === item.name) {
      return;
    }
    // 检查是否包含特殊符号，支持所有语言的文字、数字、下划线、中划线
    // biome-ignore lint/complexity/useRegexLiterals: ts 配置没有 target 字段，用字面量会报错 This regular expression flag is only available when targeting 'es6' or later.ts(1501)
    const validNamePattern = new RegExp('^[\\p{L}\\p{N}\\p{M}_\\-]+$', 'u');
    if (!validNamePattern.test(newName)) {
      toast({
        message: `${t('creations.warning.nameInvalid')}: ${t('creations.warning.nameInvalidDescription')}`,
        type: 'error',
      });
      return;
    }
    renameFile();
  };

  const handleCancel = () => {
    setIsEditingName(false);
    setNewName(item.name);
  };

  const handleNameChange = (value: string) => {
    if (value.length > MAX_NAME_LENGTH) {
      toast({
        message: `${t('creations.warning.nameMaxLength')}: ${t('creations.warning.nameMaxLengthDescription', {
          maxLength: MAX_NAME_LENGTH,
        })}`,
        type: 'error',
      });
      return;
    }
    setNewName(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  // 监听点击空白处事件
  useEffect(() => {
    if (!isEditingName) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (editingRef.current && !editingRef.current.contains(event.target as Node)) {
        handleRename();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditingName, newName]);

  // 更新 item.name 时同步更新 newName
  useEffect(() => {
    setNewName(item.name);
  }, [item.name]);

  return {
    isEditingName,
    newName,
    renamingFile,
    editingRef,
    handleEditName,
    handleRename,
    handleCancel,
    handleNameChange,
    handleKeyDown,
  };
};
